import React from "react";
import ChapterRenderer from "../../chapterRenderers";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Button from "../../Button";
import Renderer from "../../renderer";
import ConfettiCelebration from "../../ConfettiCelebration";
import { useQuiz } from "../../../pages/quiz/hooks";
import { useProfile } from "../../../pages/profile/hooks";
import { useStudy } from "../../../pages/study/hooks";
import { useCourse } from "../../../pages/course/hooks";
import Loader from "../../Loader";
import NextChapterButton from "../../chapterRenderers/NextChapterButton";
import SectionComplete from "./SectionComplete";
import SessionRetake from "./SessionRetake";
import { failedChapterSession, chapterSessions } from "../../../pages/study/course/gateUtils";
import { clearAssessmentResult, clearAssessmentAnswers } from "../../../utils/storageHelper";
import { deleteAssessmentAnswersActivity } from "../../../store/quiz/quizBaseApi";

export const Section = ({ data, style, course, onSectionCompletion, currentSectionData, currentSection, nextSection, totalSections, onCourseComplete, chaptersCompleted, activities, gate, refreshGate }) => {
    // const { result } = useQuiz();
    // const { profile } = useProfile();
    const { setCurrentChapter: setCurrentChapterProgress, setLastChapter, setLastSection, setCurrentCourseCompleted } = useCourse();
    const { setProgress, setSynonyms, clearWordsHistory } = useStudy();
    const { updateCurrentCourseDetails, setEvaluationResult, setGradingResult } = useQuiz();
    const [chapter, setChapter] = React.useState({});
    const [currentChapter, setCurrentChapter] = React.useState(0);
    const [chapterCompleted, setChapterCompleted] = React.useState(false);
    const [buttonText, setButtonText] = React.useState('Next');
    const [courseCompleted, setCourseCompleted] = React.useState(false);
    const [sectionComplete, setSectionComplete] = React.useState(false);
    const [activityData, setActivityData] = React.useState([]);

    const [showConfetti, setShowConfetti] = React.useState(false);

    // Session gate: the failing SessionGateDetail of the chapter the student
    // just finished (null = no retake pending). Set by the advance verdict.
    const [retakeSession, setRetakeSession] = React.useState(null);
    // Bumping this remounts the SAME chapter renderer for a clean retake.
    const [retakeKey, setRetakeKey] = React.useState(0);

    // Derive the chapter count from the current section's data on every render.
    // This MUST stay reactive — a ref (the previous implementation) kept the
    // PREVIOUS section's count after a section transition, so the
    // "is this the last chapter?" / "is the section complete?" checks below ran
    // with a stale count. That mis-set lastSectionChapter.chapter, which the
    // chapter renderers use to decide auto-advance, causing a whole session to
    // be skipped.
    const totalChapters = data?.chapters?.length || 0;

    const onComplete = async () => {
        setChapterCompleted(true)
    }

    /**
     * Session-gate verdict for the CURRENT chapter, asked at every advance.
     * Refetches the gate so the just-posted CHAPTER_COMPLETE/summary rows are
     * included (renderers await those posts before calling onNextChapter).
     * Returns the failing SessionGateDetail, or null when the chapter passed.
     * On a network failure the student is never hard-stuck: fall back to the
     * last known gate, and with no gate at all, allow the advance.
     */
    const failedSessionOfCurrentChapter = async () => {
        const chapterData = data?.chapters?.[currentChapter];
        if (!chapterData) return null;
        let g = gate;
        if (refreshGate) {
            try {
                g = (await refreshGate()) || gate;
            } catch (e) {
                console.error("session gate refresh failed — falling back", e);
            }
        }
        if (!g) return null;
        return failedChapterSession(g, data?.sectionId, chapterData.chapterId);
    };

    const onConfettiClose = async () => {
        setShowConfetti(false);
        const advanced = await onNextChapter();
        if (advanced && !nextSection) setCurrentCourseCompleted(true);
        setSectionComplete(false);
    }
    React.useEffect(() => {
        const isLastSection = currentSection === totalSections - 1;
        const isLastChapter = currentChapter === totalChapters - 1;

        if (isLastSection && isLastChapter) {
            setLastSection(true);
            setLastChapter(true);
        } else {
            setLastSection(false);
            setLastChapter(false);
        }
        setLastChapter(isLastChapter)
    }, [currentChapter, currentSection, totalChapters, totalSections])

    const onNextChapter = async () => {
        // Session gate: the chapter must be PASSED (every session >= 60, or
        // attempted-but-unscoreable) before anything after it unlocks.
        // Failing forces a retake of the same chapter.
        const failed = await failedSessionOfCurrentChapter();
        if (failed) {
            setRetakeSession(failed);
            setChapterCompleted(false);
            setSectionComplete(false);
            return false;
        }

        // Clear all speech evaluation state before the chapter transition so
        // the new chapter doesn't briefly render stale highlights/counts.
        clearWordsHistory();

        if (currentChapter < totalChapters - 1) {
            setCurrentChapter(currentChapter + 1);
        } else {
            // console.log('Completed chapters for ', data?.name);

            if (onSectionCompletion && typeof onSectionCompletion === 'function') {
                onSectionCompletion();
            }

            if (currentSection == (totalSections - 1)) {
                setCourseCompleted(true);

                if (onCourseComplete && typeof onCourseComplete === 'function') {
                    await onCourseComplete();
                }
            }

            setCurrentChapter(0);
        }

        setChapterCompleted(false);
        return true;
    }

    const onRetakeStart = async () => {
        const chapterData = data?.chapters?.[currentChapter];
        // A retake simply replays the chapter: new activity rows plus a new
        // CHAPTER_COMPLETE get appended and latest-attempt scoring makes the
        // new attempt authoritative — no reset call. Clear the client-side
        // leftovers so the renderer starts clean: speech history, the cached
        // assessment result (which would otherwise short-circuit the quiz
        // with the old persisted score), and the SERVER-saved answer set
        // (the network-resilience cache — the quiz prefetches it on mount,
        // so the previous attempt's answers would prefill the retake).
        clearWordsHistory();
        // Clear the shared Redux result BEFORE remounting. The remounted
        // Assessment's auto-persist effect runs before the child Quiz resets
        // Redux; if the failed attempt's evaluationResult is still present it
        // gets re-saved to the assessment-result-{id} localStorage cache we
        // clear below, and the fresh Quiz then rehydrates straight onto the old
        // result screen instead of starting a new attempt.
        setEvaluationResult({});
        setGradingResult({ grade: null, passed: null, timeTaken: null, scored: null, total: null, activityScore: 0 });
        const assessmentIds = new Set(
            (chapterData?.assessments || []).map((a) => a?.id).filter(Boolean)
        );
        if (retakeSession?.assessmentId) assessmentIds.add(retakeSession.assessmentId);
        // Await the server-side deletes BEFORE remounting the chapter — the
        // quiz's mount-time prefetch would otherwise race the delete and
        // re-prefill. Best-effort: a network failure must not block the
        // retake (the local caches are cleared regardless).
        await Promise.all([...assessmentIds].map(async (id) => {
            clearAssessmentResult(id);
            clearAssessmentAnswers(id);
            try {
                await deleteAssessmentAnswersActivity(id);
            } catch (e) {
                console.error("Failed to clear saved answers for", id, e);
            }
        }));
        setRetakeSession(null);
        setRetakeKey((k) => k + 1);
    }

    React.useEffect(() => {
        // Last chapter of the section: the celebration must come AFTER the
        // session-gate verdict — a failed session shows the retake screen
        // instead of "Congratulations". (Non-last chapters are gated inside
        // onNextChapter, which the renderers call directly.)
        if (!chapterCompleted) return;
        const isLastChapter = currentChapter === totalChapters - 1;
        if (!isLastChapter) return;

        let cancelled = false;
        (async () => {
            const failed = await failedSessionOfCurrentChapter();
            if (cancelled) return;
            if (failed) {
                // Spurious-trigger guard: a chapter with NO attempted
                // sessions was never just finished — stale chapterCompleted
                // leaking across a section transition must not throw a fresh
                // chapter onto the retake screen.
                const chapterData = data?.chapters?.[currentChapter];
                const anyAttempted = chapterSessions(
                    gate, data?.sectionId, chapterData?.chapterId
                ).some((s) => s.attempted);
                if (!anyAttempted) {
                    setChapterCompleted(false);
                    return;
                }
                setRetakeSession(failed);
                setChapterCompleted(false);
            } else {
                setSectionComplete(true);
                setShowConfetti(true);
            }
        })();
        return () => { cancelled = true; };
    }, [chapterCompleted, currentChapter, totalChapters]);

    React.useEffect(() => {
        // Section transition: reset per-section transient state. Without
        // this, the previous section's chapterCompleted (re-armed by the
        // legacy `currentChapter === totalChapters` effect above when the
        // new section has fewer chapters) leaks into the new section and
        // fires the verdict against an unattempted first session.
        setChapterCompleted(false);
        setSectionComplete(false);
        setShowConfetti(false);
        setRetakeSession(null);
    }, [data?.sectionId]);


    React.useEffect(() => {
        if (currentChapter === totalChapters) {
            setChapter({});
            setSynonyms([]);
            setChapterCompleted(true);
            setButtonText('Start next section');
        } else if (currentChapter == totalChapters - 1) {
            setSectionComplete(true);
            setButtonText('Finish');
        } else {
            setButtonText('next-chapter');
        }

        if (!data || !data.chapters || !data.chapters.length) return;

        if (data?.chapters[currentChapter]) {
            let _chapter = data.chapters[currentChapter];
            setChapter(_chapter);
            setProgress({ chapter: _chapter.chapterId });
            setCurrentChapterProgress(_chapter);
            updateCurrentCourseDetails({ chapterId: _chapter.chapterId, sectionId: currentSectionData.sectionId, courseId: course.id })
        }
    }, [currentChapter, totalChapters]);

    React.useEffect(() => {
        // Check if this is a single chapter section
        if (totalChapters === 1) {
            if (chapterCompleted) {
                setSectionComplete(true);
                setButtonText('Finish');
            }
        }
    }, [chapterCompleted, totalChapters]);

    React.useEffect(() => {
        if (activities.length) {
            setActivityData(activities);
        }
    }, [activities]);


    React.useEffect(() => {
        // Effect for restoring activities to the course
        if (!chaptersCompleted.length) {
            chaptersCompleted = [];
        }

        if (!data || !data.chapters) {
            return;
        }

        // `completed` now means PASSED (session gate). Resume at the first
        // non-passed chapter — landing ON a failed chapter for its retake,
        // instead of skipping past it to `lastCompletedIndex + 1`.
        let _chapterIndex = data.chapters.findIndex(c => !c.completed);
        if (_chapterIndex === -1) {
            _chapterIndex = 0;
        }

        // _chapterIndex = 1
        // console.log("chapter index", _chapterIndex, data.chapters)
        const _chapter = data.chapters[_chapterIndex];

        if (_chapter) {
            setChapter(_chapter);
            setCurrentChapter(_chapterIndex);
            setProgress({ chapter: _chapter.chapterId });
            setCurrentChapterProgress(_chapter);
        }

    }, [chaptersCompleted, data]);

    if (!Object.keys(chapter).length) return <Loader message={'Loading course, please wait...'} sx={{ minHeight: '100%' }} />

    if (retakeSession) {
        return <SessionRetake
            session={retakeSession}
            chapterName={data?.chapters?.[currentChapter]?.name}
            onRetry={onRetakeStart}
        />
    }

    if (showConfetti) {
        return <SectionComplete data={data} course={course} onContinue={onConfettiClose} nextSection={nextSection} />
    }

    return (
        <div style={{ ...style, ...styles.container }}>

            {/* <Renderer content={data?.description} style={styles.renderer}
                contentType={'text'}
                templateType={'top-and-bottom'} /> */}


            {Object.keys(chapter).length > 0 && <ChapterRenderer
                key={`${chapter.chapterId}-${retakeKey}`}
                onComplete={onComplete}
                chapterType={chapter.chapterType}
                activities={activityData}
                courseId={course?.id}
                sectionId={data?.sectionId}
                sectionType={data?.sectionType}
                data={chapter}
                onNextChapter={onNextChapter}
            />}

            {/* {!courseCompleted && chapterCompleted && !sectionComplete && (
                <div style={styles.btnContainer}>
                    {buttonText === 'next-chapter' ? <NextChapterButton onClick={onNextChapter} /> : <Button
                        icon={<NavigateNextIcon />}
                        onClick={onNextChapter}
                        variant="contained"
                        size="lg"
                        placeholder={buttonText}
                        sx={{
                            minWidth: '100px',
                            background: 'var(--mui-palette-primary-button)',
                            p: 1,
                            my: 4
                        }}
                    />}

                </div>
            )} */}

            {/* <ConfettiCelebration onClose={onConfettiClose} open={showConfetti} message={'You have completed all the chapters - ' + data?.name} /> */}
        </div>
    )
}

const styles = {
    container: {
        position: 'relative',
        height: 'calc(100vh - 180px)',
        // height: '100%',
        // minHeight: '300px',
    },
    renderer: {
        color: 'var(--mui-palette-text-primary)',
        width: '100%'
    },
    btnContainer: {
        display: 'flex',
        justifyContent: 'center',
        // marginTop: '40px'
    }
}